import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import * as d3 from 'd3';
import {ScaleLinear} from 'd3';
import React from 'react';

import {NucleotideType} from 'biojs/BioConstants';
import {ModifiedNucleotideAttributes, SequenceType} from 'constants/SequenceConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {ModPatternTemplateOligoQuery} from 'modbio/bulk-edit-mods/__generated/ModPatternTemplateOligoQuery';
import {DEFAULT_PATTERN_HIGHLIGHT_FILL} from 'modbio/bulk-edit-mods/ModPatternConstants';
import {
  AXIS_VMARGIN,
  FONT_SIZE,
  getWidthForBaseCount,
  LETTER_SPACING,
  StaticAxis,
  StaticBases,
} from 'modbio/polymer-maps/OligoMap';
import {widthBetweenLetters} from 'molbio/maps/linear/LinearMapUtil';
import {ModifiedNucleotidesVisualization} from 'molbio/maps/linear/ModifiedNucleotides';
import {PresentationalNucleotidesAxisOverlay} from 'molbio/maps/linear/NucleotidesAxisOverlay';
import Type from 'molbio/maps/Type';
import {MonomerQuery} from 'molbio/monomers/__generated/MonomerQuery';
import {MONOMER_QUERY} from 'molbio/monomers/MonomerConstants';
import {FONT_FAMILY_MONOSPACED} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

export const MOD_PATTERN_TEMPLATE_QUERY = gql`
  query ModPatternTemplateOligoQuery($id: StrictString!) @skipAudit @engComponent(name: MODIFIED_BIOLOGICS) {
    sequence(id: $id) {
      ... on SequenceNode {
        id
        bases
        nucleotideType
        modifiedNucleotides {
          sugarMonomerId
          baseMonomerId
          phosphateMonomerId
          position
        }
      }
    }
  }
`;

export const BULK_EDIT_MODS_OLIGO_MAP_ROW_HEIGHT = 70;

export interface QueryingModPatternTemplateOligoMapProps {
  oligoId: string;
  enabledLayers?: Array<PatternMapLayer>;
}

export function QueryingModPatternTemplateOligoMap({
  oligoId,
  enabledLayers,
}: QueryingModPatternTemplateOligoMapProps): JSX.Element {
  return (
    <BenchlingQuery<ModPatternTemplateOligoQuery>
      query={MOD_PATTERN_TEMPLATE_QUERY}
      variables={{id: oligoId}}
      renderLoading={({refetch}) => <CenteredHorizontalThrobber />}
      fetchPolicy="network-only"
    >
      {({sequence}) => {
        if (sequence.__typename === 'InaccessibleInfoNode') {
          return null;
        }
        const width = getWidthForBaseCount(sequence.bases.length) + 2; // add 2 for trailing phosphate
        return (
          <div style={{width}}>
            <BulkEditModsOligoMap
              bases={sequence.bases}
              modifiedNucleotides={sequence.modifiedNucleotides}
              nucleotideType={sequence.nucleotideType}
              index={0}
              enabledLayers={
                enabledLayers ?? [PatternMapLayer.AXIS, PatternMapLayer.BASES, PatternMapLayer.MODS]
              }
              isTemplate={true}
            />
          </div>
        );
      }}
    </BenchlingQuery>
  );
}

function TemplateAppliedHighlights({
  templateAppliedPositions,
  xFn,
}: {
  templateAppliedPositions?: Array<number>;
  xFn: ScaleLinear<number, number>;
}): JSX.Element {
  return (
    <g>
      {templateAppliedPositions?.map((pos) => (
        <rect
          key={pos}
          className={css(styles.highlight)}
          x={xFn(pos)}
          y={0}
          width={widthBetweenLetters(xFn, 0)}
          height={AXIS_VMARGIN}
        />
      ))}
    </g>
  );
}

export enum PatternMapLayer {
  BASES = 'BASES',
  MODS = 'MODS',
  AXIS = 'AXIS',
}

export interface BulkEditModsOligoMapProps {
  bases: string;
  changedPositions?: Array<number>; // axis highlighted in purple
  templateAppliedPositions?: Array<number>; // bases highlighted in beige
  enabledLayers: Array<PatternMapLayer>;
  index: number;
  isTemplate: boolean;
  modifiedNucleotides: Array<ModifiedNucleotideAttributes>;
  nucleotideType: NucleotideType;
}

export function BulkEditModsOligoMap({
  bases,
  modifiedNucleotides,
  nucleotideType,
  index,
  changedPositions,
  templateAppliedPositions,
  enabledLayers,
  isTemplate,
}: BulkEditModsOligoMapProps): JSX.Element {
  const type = new Type(FONT_SIZE, LETTER_SPACING);
  const width = getWidthForBaseCount(bases.length);
  const xFn = d3.scaleLinear().clamp(true).domain([0, bases.length]).range([0, width]);

  return (
    <BenchlingQuery<MonomerQuery> query={MONOMER_QUERY} renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}>
      {(monomerData) => (
        <svg
          className={css(styles.wrapper)}
          {...getTestProps({
            component: 'BulkEditModsOligoMap',
            element: 'root',
            key: isTemplate ? 'templateMap' : 'targetMap',
          })}
          data-amp-safe={false}
        >
          <TemplateAppliedHighlights templateAppliedPositions={templateAppliedPositions} xFn={xFn} />
          {enabledLayers.includes(PatternMapLayer.MODS) && (
            <ModifiedNucleotidesVisualization
              bases={bases}
              sequenceMods={modifiedNucleotides}
              monomers={monomerData.monomers}
              isOligo={true}
              nucleotideType={nucleotideType}
              xFn={xFn}
              sliceStart={0}
              sliceEnd={bases.length}
              layout={{vMargin: 0}}
              boxHeightToDNA={0}
              showNucleotideStructures={true}
              keyPrefix={`bulkeditmodstarget${index}`}
              displayBasesAsNatural={!enabledLayers.includes(PatternMapLayer.BASES)}
            />
          )}
          {enabledLayers.includes(PatternMapLayer.BASES) && (
            <StaticBases
              bases={bases}
              modifiedNucleotides={modifiedNucleotides}
              nucleotideType={nucleotideType}
              sequenceType={SequenceType.OLIGO}
              fontSize={type.fontSize}
              letterSpacing={type.letterSpacing}
              vMargin={10}
              isTemplate={isTemplate}
            />
          )}
          {enabledLayers.includes(PatternMapLayer.AXIS) && (
            <>
              <StaticAxis
                basesLength={bases.length}
                charWidth={type.charWidth()}
                xFn={xFn}
                vMargin={AXIS_VMARGIN}
              />
              <PresentationalNucleotidesAxisOverlay
                xFn={xFn}
                sliceStart={0}
                sliceEnd={bases.length}
                layout={{vMargin: AXIS_VMARGIN}}
                bases={bases}
                nucleotideType={nucleotideType}
                selectedPositions={new Set(changedPositions ?? [])}
                hoveredMonomerIds={[]}
                hoveredNucleotideIdx={null}
                monomers={monomerData.monomers}
                modifiedNucleotides={modifiedNucleotides}
              />
            </>
          )}
        </svg>
      )}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    fontFamily: FONT_FAMILY_MONOSPACED,
    shapeRendering: 'crispEdges',
    width: '100%',
    height: 70,
  },
  noPointerEvents: {
    pointerEvents: 'none',
  },
  highlight: {
    fill: DEFAULT_PATTERN_HIGHLIGHT_FILL,
  },
});
